import type { AuthenticatedSession } from '@cf/shared';
import { authLoginOkSchema, authLoginResponseSchema, authSessionResponseSchema } from '@cf/shared';
import { API_BASE_URL } from '../data/apiBase';
import type { IAuthClient, MfaChallenge, SignInResult } from './authClient';
import { SignInError, signInErrorFromResponse } from './authClient';

/**
 * Shown when the pool hands back a session for a password alone. Names the
 * cause without leaking pool configuration to an unauthenticated caller: an
 * operator reads the precise reason in the API/CloudWatch logs, the person at
 * the screen reads a sentence that does not invite them to retry.
 */
export const MFA_NOT_ENFORCED_MESSAGE =
  'Two-step verification could not be completed, so you have not been signed in. ' +
  'Please contact support — this needs fixing on the account, not by trying again.';

/** Label shown in the user's authenticator app for the enrolled TOTP secret. */
const TOTP_ISSUER = 'Enzo';

/**
 * Server-MEDIATED Cognito auth: the browser posts credentials to the API, and
 * the API runs the Cognito `ADMIN_USER_PASSWORD_AUTH` + software-token MFA flow
 * (see apps/api/src/auth/cognito-login.ts). Chosen over the browser-direct SRP
 * client (CognitoAuthClient) when the topology is web -> API -> Cognito — the
 * shape the deployed service actually uses, and the one whose drift we are
 * replacing with in-repo code.
 *
 * Two steps, behind the same IAuthClient seam as every other provider:
 *   1. signIn(email, password) -> POST /auth/login -> RETURNS the opaque MFA
 *      challenge (email + session) in its result (a password alone never yields
 *      a token — MFA is required). The caller threads that challenge into step 2.
 *   2. completeMfa/setupMfa(code, challenge) -> POST /auth/mfa[-setup] -> stores
 *      the ID-token bearer, then resolves the session from /auth/me.
 */
export class ApiCognitoAuthClient implements IAuthClient {
  // Instance state: the bearer, set once a TOTP step (or an MFA-less login or a
  // silent resume) succeeds, plus the renewal timer that keeps it fresh. The
  // refresh token itself NEVER appears here — it lives in an HttpOnly cookie
  // the server sets on /auth/mfa and reads on /auth/refresh, so session
  // persistence adds nothing readable to the page. The MFA challenge (email +
  // session) is NOT held here: it is returned from signIn and threaded back
  // into completeMfa/setupMfa, so the verify call can never go missing between
  // the login screen and the MFA screen.
  private token: string | null = null;
  private renewalTimer: ReturnType<typeof setTimeout> | null = null;

  async signIn(email: string, password: string): Promise<SignInResult> {
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });
    if (!res.ok) {
      // Structured failure: field-level 422s attach to their own inputs, a 401
      // becomes the no-oracle combination sentence, other server-safe messages
      // (lockout "Too many attempts…", "password must be reset") pass through
      // verbatim — and no raw HTTP status ever reaches the card.
      throw await signInErrorFromResponse(res);
    }
    const body = (await res.json()) as { data: unknown };
    const login = authLoginResponseSchema.parse(body.data);

    if (login.status === 'ok') {
      // The pool issued tokens from a PASSWORD ALONE — it is not enforcing MFA.
      // REFUSE, matching CognitoAuthClient's long-standing policy for the same
      // edge ("a password alone must NOT yield a session ... we never skip
      // TOTP") and the IAuthClient contract that step 1 never yields a session.
      //
      // This previously stored the bearer and returned a null challenge, which
      // rendered the ordinary "Two-step verification" screen and then IGNORED
      // whatever code was typed — so any six digits signed the user in, while
      // both the screen and (via COGNITO_MFA_ENFORCED) the API reported the
      // session as MFA-proven, though the token carried no `amr` claim. That is
      // the opposite of failing closed: it manufactured the appearance of a
      // second factor that had not happened.
      //
      // The bearer is deliberately NOT retained: nothing may later resolve a
      // session from a login that skipped MFA.
      this.token = null;
      throw new SignInError(MFA_NOT_ENFORCED_MESSAGE);
    }
    if (login.status === 'mfa_setup') {
      // First login: no authenticator yet. Return the enrolment challenge
      // (email + session) for the follow-up call, plus the secret + otpauth URI
      // so the screen can render a QR to scan.
      return {
        kind: 'mfa-setup',
        secret: login.secret,
        otpauthUri: buildOtpauthUri(email, login.secret),
        challenge: { email, session: login.session },
      };
    }
    // Returning user: hand back the challenge to answer at /auth/mfa.
    return { kind: 'mfa-required', challenge: { email, session: login.session } };
  }

  async completeMfa(code: string, challenge?: MfaChallenge | null): Promise<AuthenticatedSession> {
    // No challenge means no MFA step was ever issued, so there is nothing to
    // verify and no session to grant. This used to resolve a session from a
    // stored bearer and ignore `code` entirely — the bypass that made any six
    // digits work. signIn now refuses that case outright, so this is
    // unreachable defence rather than a supported path; it stays because an
    // unreachable refusal is cheap and a reachable bypass is not.
    if (!challenge) {
      throw new SignInError(MFA_NOT_ENFORCED_MESSAGE);
    }
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/mfa`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: challenge.email, code, session: challenge.session }),
      // Accept the HttpOnly refresh cookie the server sets alongside the bearer.
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(await challengeErrorMessage(res));
    }
    const body = (await res.json()) as { data: unknown };
    const issued = authLoginOkSchema.parse(body.data);
    this.token = issued.idToken;
    this.scheduleRenewal(issued.expiresIn);
    return this.resolveSession();
  }

  /**
   * First-login enrolment — verify the first code from the just-scanned secret.
   * Uses the enrolment `challenge` (email + session) threaded from `signIn`'s
   * `mfa-setup` result, so the enrolment call never depends on hidden state.
   */
  async setupMfa(code: string, challenge?: MfaChallenge | null): Promise<AuthenticatedSession> {
    if (!challenge) {
      throw new Error('Sign in first.');
    }
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/mfa-setup`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: challenge.email, code, session: challenge.session }),
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(await challengeErrorMessage(res));
    }
    const body = (await res.json()) as { data: unknown };
    const issued = authLoginOkSchema.parse(body.data);
    this.token = issued.idToken;
    this.scheduleRenewal(issued.expiresIn);
    return this.resolveSession();
  }

  /**
   * Self-service password reset, step one (GOAL 2026-09-13 item 4). The API
   * answers the same 200 for a known and an unknown address, so nothing here
   * can reveal whether an account exists; a throttle or an invalid request
   * surfaces as a sentence the page can show.
   */
  async requestPasswordReset(email: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/password-reset/request`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      throw await signInErrorFromResponse(res);
    }
  }

  /** Step two: the emailed code and the new password. MFA is untouched. */
  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/password-reset/confirm`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, code, newPassword }),
    });
    if (!res.ok) {
      const err = await signInErrorFromResponse(res);
      // A 401 here is "code not accepted", not the sign-in combination sentence.
      throw res.status === 401
        ? new SignInError(
            'That code was not recognised or has expired. Request a new code and try again.',
            {},
            err.code,
          )
        : err;
    }
  }

  async signOut(): Promise<void> {
    this.token = null;
    if (this.renewalTimer !== null) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
    // Drop the server-side refresh cookie so no silent renewal outlives the
    // sign-out. Best effort: a network failure must not block signing out.
    try {
      await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // The bearer is gone either way; the cookie dies at its 12h Max-Age.
    }
  }

  getToken(): string | null {
    return this.token;
  }

  /**
   * Resume a persisted session after a page refresh or in a new tab: exchange
   * the HttpOnly refresh cookie (if the browser holds a live one) for a fresh
   * bearer, then resolve the session exactly like a completed TOTP step. A 401
   * simply means "no session to resume" — the caller shows the login screen.
   * This is what makes F5 mid-meeting a non-event (2026-08-23 review P0 #2):
   * the TOTP challenge ran when the cookie was minted, and every MFA layer
   * (route gate, API middleware, DB session context) still sees the same
   * verified Cognito identity.
   */
  async resume(): Promise<AuthenticatedSession | null> {
    const issued = await this.refreshBearer();
    if (issued === null) return null;
    return this.resolveSession();
  }

  /** Exchange the refresh cookie for a fresh bearer; null when there is none. */
  private async refreshBearer(): Promise<{ expiresIn: number } | null> {
    let res: Response;
    try {
      res = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      return null; // offline — treat as no resumable session
    }
    if (!res.ok) return null;
    const body = (await res.json()) as { data: unknown };
    const issued = authLoginOkSchema.parse(body.data);
    this.token = issued.idToken;
    this.scheduleRenewal(issued.expiresIn);
    return { expiresIn: issued.expiresIn };
  }

  /**
   * Renew the bearer two minutes before it expires, so a 90-minute client
   * meeting never hits the ~1h Cognito ID-token wall. If the silent renewal
   * fails (cookie hit its 12h Max-Age, token revoked), the bearer simply runs
   * out and the data layer's session-expiry handling takes over — never a
   * mid-meeting surprise TOTP from this timer itself.
   */
  private scheduleRenewal(expiresInSeconds: number): void {
    if (this.renewalTimer !== null) clearTimeout(this.renewalTimer);
    const delayMs = Math.max((expiresInSeconds - 120) * 1000, 30_000);
    this.renewalTimer = setTimeout(() => {
      void this.refreshBearer();
    }, delayMs);
  }

  /** Resolve firm/adviser/role/MFA for the stored bearer via the API. */
  private async resolveSession(): Promise<AuthenticatedSession> {
    if (this.token === null) {
      throw new Error('Sign in first.');
    }
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
      headers: { authorization: `Bearer ${this.token}` },
    });
    if (!res.ok) {
      throw new Error('Could not establish the session — please sign in again.');
    }
    const body = (await res.json()) as { data: unknown };
    return authSessionResponseSchema.parse(body.data) as AuthenticatedSession;
  }
}

/**
 * Read the first error message from a failed response's `fail` envelope, so the
 * user sees the API's specific, safe wording (lockout / reset / not-confirmed)
 * instead of a bare status. Falls back if the body is missing or unparseable.
 */
async function messageFromEnvelope(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { errors?: { message?: unknown }[] };
    const message = body.errors?.[0]?.message;
    return typeof message === 'string' && message.length > 0 ? message : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Friendly, recoverable message for a failed TOTP challenge — shared by the
 * returning-user (/auth/mfa) and first-login enrolment (/auth/mfa-setup) paths
 * so the safe wording is written once. A 401 is the EXPECTED "wrong or expired
 * code" case: say so plainly and invite a retry with the CURRENT code (they
 * refresh every 30 seconds). Any other failure surfaces the server's own safe
 * message when present (e.g. a lockout/throttle with Retry-After) and otherwise
 * a neutral try-again — never a raw HTTP status to a lay adviser mid-enrolment.
 */
async function challengeErrorMessage(res: Response): Promise<string> {
  if (res.status === 401) {
    return 'That code didn’t match, or it has just expired. Your authenticator app shows a new 6-digit code every 30 seconds — enter the current one and try again.';
  }
  return messageFromEnvelope(
    res,
    'We couldn’t verify that code just now. Please wait for a fresh code and try again.',
  );
}

/** Build the `otpauth://` URI an authenticator app scans to enrol the secret. */
export function buildOtpauthUri(email: string, secret: string): string {
  const label = `${encodeURIComponent(TOTP_ISSUER)}:${encodeURIComponent(email)}`;
  const params = new URLSearchParams({
    secret,
    issuer: TOTP_ISSUER,
    algorithm: 'SHA1',
    digits: '6',
    period: '30',
  });
  return `otpauth://totp/${label}?${params.toString()}`;
}
